/**
 * Query-language token data — command definitions and regex patterns.
 *
 * Extracted from QuerySyntaxHighlightingExtension so the same source of truth
 * can drive both the live editor decorations and the static documentation
 * highlighter. Pure data only; no ProseMirror/DOM dependencies.
 */

// Define the structure for command definitions
export interface CommandDefinition {
  params: string[]
}

// Define the type for command definitions object
export type CommandDefinitions = Record<string, CommandDefinition>

// Complete command definitions with parameters
export const commandDefs: CommandDefinitions = {
  PDUMP: { params: [] },
  DUMP: { params: ["<name>"] },
  SDUMP: { params: ["<name>"] },
  INFO: { params: ["<name>"] },
  OUTPUT: { params: ["<name>"] },
  OUTPUTJ: { params: ["<name>"] },
  XTAB: { params: ["<name>", "<var1>", "<var2>"] },
  DTAB: { params: ["<name>", "<var1>", "<var2>"] },
  QTABLE: { params: ["<name>"] },
  REMOVE: { params: ["<name>"] },
  NOT: { params: ["<field>"] },
  BLANKTRUE: { params: ["<field>"] },
  BLANKFALSE: { params: ["<field>"] },
  BLANKZERO: { params: ["<field>"] },
  SETBLANK: { params: ["<field>"] },
  SCOPE: { params: ["<qtable>"] },
  REMEMBER: { params: ["<qtable>"] },
  RECALL: { params: ["<qtable>"] },
  FORGET: { params: ["<qtable>"] },
  COPY: { params: ["<to>", "<from>"] },
  COPYSTRUCT: { params: ["<to>", "<from>"] },
  APPEND: { params: ["<to>", "<from>"] },
  ASSIGN: { params: ["<to>", "<from>"] },
  FIELD: { params: ["<qtable::field>", "<type>"] },
  ADD: { params: ["<target>", "<source>"] },
  SUBTRACT: { params: ["<target>", "<source>"] },
  MULT: { params: ["<target>", "<source>"] },
  DIV: { params: ["<target>", "<source>"] },
  AND: { params: ["<target>", "<source>"] },
  OR: { params: ["<target>", "<source>"] },
  CODEBOOK: { params: [] },
  RESET: { params: [] },
  CLEAR: { params: [] },
  STOP: { params: [] },
  FIND: { params: ["<search>"] },
  FINDR: { params: ["<regex>"] },
  PROJECT: { params: ["<json>"] },
  ALIAS: { params: ["<name>", "<target>"] },
  FFIELD: { params: ["<name>", "<table>"] },
  USEWEIGHTS: { params: ["<column>"] },
  CALCWEIGHTS: { params: ["<field>", "<factor>"] },
  APPENDSTRUCT: { params: ["<target>", "<source>"] },
  RENAME: { params: ["<name>", "<new_label>"] },
  CALC: { params: ["<target>", "<expression>"] },
  CAT: { params: ["<target_table>", "<source_field>", "<spec>"] },
  // The engine accepts these with zero arguments (EXCL/RESPID clear their
  // setting, HELP/TEST take an optional topic, LINEAGE takes none), so the
  // required minimum is 0 — anything else would squiggle a valid line.
  EXCL: { params: [] },
  RESPID: { params: [] },
  HELP: { params: [] },
  TEST: { params: [] },
  LINEAGE: { params: [] },
}

// Define the type for regex patterns
export type PatternType =
  | "comment"
  | "command"
  | "type"
  | "qtablePath"
  | "boolean"
  | "condition"
  | "keyword"
  | "clause"

// Longest-first so a longer command wins the alternation (APPENDSTRUCT over
// APPEND, COPYSTRUCT over COPY, OUTPUTJ over OUTPUT). Derived from
// commandDefs so the highlighted set can never drift from the checked set.
const commandAlternation = Object.keys(commandDefs)
  .sort((a, b) => b.length - a.length || a.localeCompare(b))
  .join("|")

/**
 * Regex patterns for syntax.
 *
 * `keyword` vs `clause` vs `condition` — the split, so no two ever decorate
 * the same range:
 *   keyword   line-leading preprocessor control flow (FOR/IF/ENDIF/…), never
 *             followed by a colon.
 *   condition the filter-clause colon forms `IF:` / `WHERE:` plus the
 *             identifier that follows them, wherever they appear.
 *   clause    every other clause keyword, mid-line only (a line-leading one
 *             is a command or a preprocessor keyword instead); WHERE and IF
 *             are excluded here when a colon follows, leaving those to
 *             `condition`.
 */
export const patterns: Record<PatternType, RegExp> = {
  comment: /^#.*/gm,
  // Lookbehind rather than `^` so indented commands still highlight.
  command: new RegExp(`(?<=^[ \\t]*)(?:${commandAlternation})\\b`, "g"),
  type: /\b(INT|REAL|TEXT|BOOL)\b/g,
  qtablePath: /(?:<<[^>]+>>)|(?:\b[\w_]+::[\w\d\s-]+\b)/g,
  boolean: /\b(true|false)\b/gi,
  condition: /\b(?:IF|WHERE):(?:[ \t]*[\w_]+)?/g,
  keyword:
    /(?<=^[ \t]*)(?:DEFINE|UNDEF|IFDEF|IFNDEF|IF|ELSE|ENDIF|FOR|ENDFOR)\b(?!:)/g,
  clause:
    /(?<=\S[\s\S]*)\b(?:(?:WHERE|IF)\b(?!:)|(?:UNIVERSE|OPERATORS|COUNT|SUM|AVERAGE|NOCONVERSION|EXTENDED|EXCL|IN)\b)/g,
}
